// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
import * as React from 'react';
import { Check, ChevronDown, Copy, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { copyTextToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';
import { formatAcquisitionGuideSteps, type AcquisitionGuideStep } from '@greenlight/shared';

/** A guide step from the catalog, where a step with no details carries null. */
type GuideStep = Omit<AcquisitionGuideStep, 'details'> & { details?: string | null };

export interface AcquisitionGuidePanelProps {
  credentialKind: string;
  instructions: string;
  docsUrl: string;
  docsLabel?: string;
  steps?: readonly GuideStep[];
  stepsTitle?: string;
  /** SQL a step has IT run upstream, already filled with the form's values. */
  statement?: { title: string; text: string };
  /** Start with the stepped guide expanded. */
  defaultOpen?: boolean;
}

/**
 * Catalogued credential-acquisition guidance on the registration form.
 * Short copy stays inline; long stepped guides collapse behind one accordion.
 */
export function AcquisitionGuidePanel({
  credentialKind,
  instructions,
  docsUrl,
  docsLabel = 'Acquisition docs',
  steps,
  stepsTitle = 'Setup guide',
  statement,
  defaultOpen = false,
}: AcquisitionGuidePanelProps): React.JSX.Element {
  const kindLabel = credentialKind.replace(/_/g, ' ');

  return (
    <div
      className="space-y-2 rounded-gl border border-border bg-muted/30 p-3"
      data-testid="acquisition-guide"
    >
      <p className="text-[11.5px] font-medium text-foreground">Credential: {kindLabel}</p>

      {steps && steps.length > 0 ? (
        <details
          open={defaultOpen}
          className="group overflow-hidden rounded-gl border border-border bg-background"
          data-testid="acquisition-guide-steps"
        >
          <summary
            className={cn(
              'flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-[12px] font-medium text-foreground',
              'marker:content-none [&::-webkit-details-marker]:hidden',
              'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            )}
          >
            <span className="min-w-0 flex-1 text-left leading-snug">{stepsTitle}</span>
            <span className="shrink-0 text-[11px] font-normal text-muted-foreground">
              {steps.length} steps
            </span>
            <ChevronDown
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <GuideSteps steps={steps} className="border-t border-border px-3 py-3" />
          {statement && (
            <div className="space-y-1 border-t border-border px-3 py-3">
              <p className="text-[11.5px] font-medium text-foreground">{statement.title}</p>
              <pre
                className="overflow-x-auto rounded-gl bg-muted px-2.5 py-2 font-mono text-[11px] leading-relaxed text-foreground"
                data-testid="acquisition-guide-statement"
              >
                {statement.text}
              </pre>
            </div>
          )}
        </details>
      ) : (
        <p className="whitespace-pre-line text-[11.5px] leading-relaxed text-muted-foreground">
          {instructions}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <a
          href={docsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-link hover:underline"
        >
          {docsLabel}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
        {steps && steps.length > 0 && (
          <CopyStepsButton title={stepsTitle} steps={steps} {...(statement ? { statement } : {})} />
        )}
      </div>
    </div>
  );
}

/** Numbered guide steps, each a short instruction with its explanation behind "Details". */
export function GuideSteps({
  steps,
  className,
}: {
  steps: readonly GuideStep[];
  className?: string;
}): React.JSX.Element {
  return (
    <ol className={cn('space-y-3', className)}>
      {steps.map((step, index) => (
        <li key={step.title} className="flex gap-2.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[10px] text-foreground">
            {index + 1}
          </span>
          <div className="min-w-0 space-y-0.5">
            <p className="text-[12px] font-medium leading-snug text-foreground">{step.title}</p>
            <p className="whitespace-pre-line text-[11.5px] leading-relaxed text-muted-foreground">
              {step.body}
            </p>
            {step.details && (
              <details className="group/details" data-testid="guide-step-details">
                <summary
                  className={cn(
                    'inline-flex cursor-pointer list-none items-center gap-1 text-[11px] font-medium text-link',
                    'marker:content-none [&::-webkit-details-marker]:hidden hover:underline',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  Details
                  <ChevronDown
                    className="h-3 w-3 transition-transform group-open/details:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <p className="mt-1 whitespace-pre-line text-[11.5px] leading-relaxed text-muted-foreground">
                  {step.details}
                </p>
              </details>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The steps as plain text, explanations included, for pasting into a ticket or an agent. */
export function guideStepsText(
  title: string,
  steps: readonly GuideStep[],
  statement?: { title: string; text: string },
): string {
  const numbered = formatAcquisitionGuideSteps(
    steps.map(({ title: stepTitle, body, details }) => ({
      title: stepTitle,
      body,
      ...(details ? { details } : {}),
    })),
  );
  const tail = statement ? [`${statement.title}\n${statement.text}`] : [];
  return [title, numbered, ...tail].join('\n\n');
}

/** Copies a guide's steps, with their details, as plain text. */
export function CopyStepsButton({
  title,
  steps,
  statement,
}: {
  title: string;
  steps: readonly GuideStep[];
  statement?: { title: string; text: string };
}): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-2 text-[11.5px] text-muted-foreground"
      onClick={() =>
        void copyTextToClipboard(guideStepsText(title, steps, statement)).then(setCopied)
      }
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {copied ? 'Copied' : 'Copy steps'}
    </Button>
  );
}
